import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronsLeft, ChevronsRight, LogOut, Menu, UserCog, Stethoscope, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AccountDialog } from "@/components/app/AccountDialog";

const NavItem = ({ item, collapsed, onNavigate }) => {
  const link = (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      data-testid={item.testid}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          isActive ? "bg-slate-800 text-white shadow-inner" : "text-slate-400 hover:text-white hover:bg-slate-800/60",
          collapsed && "justify-center px-2"
        )
      }
    >
      <item.icon className="h-[18px] w-[18px] shrink-0" />
      {!collapsed && <span className="truncate" title={item.label}>{item.label}</span>}
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
};

const SidebarBody = ({ sections, collapsed, spaceLabel, brand, onNavigate }) => (
  <div className="flex flex-col h-full">
    <div className={cn("flex items-center gap-3 px-4 h-16 border-b border-slate-800", collapsed && "justify-center px-2")}>
      {brand?.logo ? (
        <img src={brand.logo} alt="" className="h-9 w-9 rounded-lg object-cover bg-white" />
      ) : (
        <div className="h-9 w-9 rounded-lg bg-sky-600 flex items-center justify-center shrink-0">
          <Stethoscope className="h-5 w-5 text-white" />
        </div>
      )}
      {!collapsed && (
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate font-heading">{brand?.name || "MediSaaS"}</p>
          <p className="text-[11px] uppercase tracking-widest text-sky-400 truncate">{spaceLabel}</p>
        </div>
      )}
    </div>
    <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
      {sections.map((s) => (
        <div key={s.label} className="space-y-1">
          {!collapsed && <p className="px-3 mb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{s.label}</p>}
          {s.items.map((it) => (
            <NavItem key={it.to} item={it} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </nav>
  </div>
);

const TenantSwitcher = () => {
  const { me, setMe } = useAuth();
  const navigate = useNavigate();
  if (!me?.memberships || me.memberships.length < 2) return null;
  const switchTo = async (id) => {
    try {
      const { data } = await api.post("/auth/switch-tenant", { tenant_id: id });
      setMe(data);
      navigate("/cabinet");
      toast.success("Cabinet changé");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="hidden sm:flex" data-testid="tenant-switcher">
          <ArrowLeftRight className="h-4 w-4 mr-2" /> {me.tenant?.name}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {me.memberships.map((m) => (
          <DropdownMenuItem key={m.tenant_id} onClick={() => switchTo(m.tenant_id)} data-testid={`tenant-switch-${m.tenant_id}`}>
            {m.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const UserMenu = ({ roleLabel }) => {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [account, setAccount] = useState(false);
  const initials = (me.user.name || me.user.email).split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-3 rounded-full pl-1 pr-3 py-1 hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
            <span className="h-8 w-8 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center">{initials}</span>
            <span className="hidden md:block text-left">
              <span className="block text-sm font-semibold text-slate-900 leading-tight">{me.user.name || me.user.email}</span>
              <span className="block text-xs text-slate-500" data-testid="user-role-label">{roleLabel}</span>
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <p className="text-sm font-semibold">{me.user.name}</p>
            <p className="text-xs text-slate-500 truncate">{me.user.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setAccount(true)} data-testid="user-menu-account">
            <UserCog className="h-4 w-4 mr-2" /> Mon compte et sécurité
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={async () => {
              await logout();
              navigate("/connexion");
            }}
            className="text-red-600"
            data-testid="user-menu-logout"
          >
            <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AccountDialog open={account} onOpenChange={setAccount} />
    </>
  );
};

export const Shell = ({ sections, spaceLabel, roleLabel, brand, children }) => {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebar-collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const current = sections.flatMap((s) => s.items).filter((i) => (i.end ? location.pathname === i.to || location.pathname === `${i.to}/` : location.pathname.startsWith(i.to))).sort((a, b) => b.to.length - a.to.length)[0];

  useEffect(() => {
    localStorage.setItem("sidebar-collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setCollapsed((c) => !c);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <TooltipProvider delayDuration={100}>
      <div className="min-h-screen flex bg-slate-50">
        <aside
          className={cn("hidden lg:flex flex-col bg-slate-900 border-r border-slate-800 sticky top-0 h-screen transition-[width] duration-300", collapsed ? "w-[72px]" : "w-64")}
          data-testid="sidebar"
        >
          <SidebarBody sections={sections} collapsed={collapsed} spaceLabel={spaceLabel} brand={brand} />
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="flex items-center justify-center gap-2 h-12 border-t border-slate-800 text-slate-400 hover:text-white text-xs transition-colors"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Réduire le menu</>}
          </button>
        </aside>
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="p-0 w-72 bg-slate-900 border-slate-800">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarBody sections={sections} collapsed={false} spaceLabel={spaceLabel} brand={brand} onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex-1 min-w-0 flex flex-col">
          <header className="backdrop-blur-md bg-white/80 border-b border-slate-200/80 sticky top-0 z-40 h-16 flex items-center justify-between gap-3 px-4 sm:px-8">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
              <div className="text-sm text-slate-500 truncate" data-testid="breadcrumb">
                <span className="hidden sm:inline">{spaceLabel}</span>
                {current && <span className="hidden sm:inline mx-2 text-slate-300">/</span>}
                <span className="font-semibold text-slate-900">{current?.label}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TenantSwitcher />
              <UserMenu roleLabel={roleLabel} />
            </div>
          </header>
          <main className="flex-1 px-4 sm:px-8 py-8 max-w-[1400px] w-full mx-auto">{children}</main>
        </div>
      </div>
    </TooltipProvider>
  );
};
